import React from 'react';
// --- external imports ------

import { isRetryableAuthError } from '~/authentication/lib/classifyAuthError';
import AuthBackdrop from '~/authentication/AuthBackdrop';
import { ModalBackground, TitleText } from '~/authentication/AuthAssets';
import { LoadingCursor } from '~/containers/GameAsset/cursors';
// --- internal imports ------

import { resolveAuthErrorCopy } from './authErrorCopy';
import {
	BodyText,
	RetryButton,
	RetryButtonText,
	BackToLoginButton,
} from './AuthErrorScreenAssets';
// --- local imports ------

/**
 * The one place a failed authentication is explained to the player.
 *
 * Which failures reach it is decided by `classifyAuthError`: a session that
 * merely ended never gets here, because the login form is already the answer. A
 * lapsed licence or a revoked account does, since no amount of signing in will
 * change it, and so does an unreachable user system — that one keeps its retry,
 * because the account behind it is fine.
 *
 * `onBack` is required rather than optional. It used to fall back to a router
 * link, which meant leaving the screen mid-retry: the provider's chain ran on
 * for the rest of its backoff and then popped an error screen over whatever the
 * player had navigated to. Every way off this screen is a button the retry can
 * disable.
 */
function AuthErrorScreen({ error, onRetry, onBack, isRetrying }) {
	const { header, message } = resolveAuthErrorCopy(error);

	const canRetry = typeof onRetry === 'function' && isRetryableAuthError(error);

	return (
		<AuthBackdrop>
			{isRetrying && <LoadingCursor />}

			<ModalBackground
				x={528}
				y={148}
				shouldFadeIn={false}
			/>

			<TitleText
				x={644}
				y={232}
				fontSize={64}
				lineHeight={64}
				shouldFadeIn={false}
			>
				{header}
			</TitleText>

			<BodyText
				x={644}
				y={452}
				shouldFadeIn={false}
			>
				{message}
			</BodyText>

			<BackToLoginButton
				x={644}
				y={744}
				onClick={onBack}
				disabled={isRetrying}
				shouldFadeIn={false}
			>
				Back To Login
			</BackToLoginButton>

			{canRetry && (
				<>
					<RetryButton
						x={865}
						y={896}
						onClick={onRetry}
						disabled={isRetrying}
						shouldFadeIn={false}
					/>
					<RetryButtonText
						x={881}
						y={907}
						shouldFadeIn={false}
					>
						Try Again
					</RetryButtonText>
				</>
			)}
		</AuthBackdrop>
	);
}

export default AuthErrorScreen;
